import React, { FC, useContext } from 'react'
import { Box, Typography } from 'transfix-ui/core'
import { UserContext } from 'apps/fleet-planner/context/UserContext'
import { isLoadRecommendationsDigestDisabled } from 'apps/fleet-planner/utils/bookableShipmentSavedSearch'
import { Checkbox } from 'components/forms'
import { Switch, Toast, useToastState } from 'transfix-ui'
import { UserSettingsCard } from './UserSettingsCard'

export const NOTIFICATION_TIMES = [
  { label: '8am EST', value: 8 },
  { label: '12pm EST', value: 12 },
  { label: '3pm EST', value: 15 },
]

/**
 * Email preference settings for load recommendations.
 */
export const NotificationsCard: FC = () => {
  const { updateUserSettings, userData } = useContext(UserContext)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { user } = userData || {}
  const { email, settings } = user || {}
  const { loadRecommendationsDigest = [] } = settings || {}
  const isDisabled = isLoadRecommendationsDigestDisabled(user || {})

  const updateLoadRecommendationsDigestSettings = async (loadRecommendationsDigest: number[]) => {
    try {
      await updateUserSettings({
        settings: {
          loadRecommendationsDigest,
        },
      })
      openToast({ toastType: 'default', toastMessage: 'Settings have been updated.' })
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: 'There was an error. Please reach out to support.',
      })
    }
  }

  const switchChangeHandler = () => {
    // If digest settings are enabled, remove all times,
    // Otherwise select all times
    const allNotificationTimes = NOTIFICATION_TIMES.map(({ value }) => value)
    const loadRecommendationsDigestPayload = isDisabled ? allNotificationTimes : []
    updateLoadRecommendationsDigestSettings(loadRecommendationsDigestPayload)
  }

  const checkboxChangeHandler = (value: number) => {
    const loadRecommendationsDigestPayload = [...loadRecommendationsDigest]
    if (!loadRecommendationsDigest.includes(value)) {
      loadRecommendationsDigestPayload.push(value)
    } else {
      loadRecommendationsDigestPayload.splice(loadRecommendationsDigestPayload.indexOf(value), 1)
    }
    updateLoadRecommendationsDigestSettings(loadRecommendationsDigestPayload)
  }

  return (
    <UserSettingsCard
      title={
        <Box mt={1}>
          <Typography component='div' variant='h3'>
            Email Preferences
          </Typography>
        </Box>
      }
      subtitle={
        <span>
          We&#39;ll send the selected emails to <span className='fs-mask'>{email}</span>
        </span>
      }>
      <Box display='flex' alignItems='center' flexDirection='row'>
        <Box>
          <Typography variant='subtitle1'>Daily ‘Fix Load Recommendations Email</Typography>
          <Typography variant='body2'>
            A digest of load recommendations that is sent up to 3 times per day
          </Typography>
        </Box>
        <Box marginLeft='auto'>
          <Switch
            label={isDisabled ? 'Off' : 'On'}
            onChange={switchChangeHandler}
            checked={!isDisabled}
          />
        </Box>
      </Box>
      <Box display='flex' flexDirection='row' pt={2} pb={1}>
        {NOTIFICATION_TIMES.map(n => {
          const { label, value } = n || {}
          const changeHandler = () => {
            checkboxChangeHandler(value)
          }
          return (
            <Box key={`notification-time-${label}`} pr={2}>
              <Checkbox
                onChange={changeHandler}
                label={label}
                checked={loadRecommendationsDigest.includes(value)}
              />
            </Box>
          )
        })}
      </Box>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </UserSettingsCard>
  )
}
